// ===================== Enemies =====================
// Pure data. `tier` + `weight` drive spawn frequency (fodder is common early;
// tougher tier-3+ foes ramp in over time). `behavior` is interpreted by the
// Game (chase | swarm | charger | shooter | brute | orbiter | phantom) and
// optional death behaviors add flavor (split / explode / shardBurst).
// `name` + `blurb` are the Bestiary entry, revealed on the first kill.
import { defineEnemy } from '../core/registries.js';

// ------------------------------- tier 1: fodder -------------------------------
defineEnemy({
  id: 'slime', sprite: 'slime', behavior: 'swarm', tier: 1, weight: 10,
  hp: 14, speed: 62, dmg: 8, r: 13, xp: 1, tint: '#63e06a',
  name: 'Meadow Slime', blurb: 'Soft, green and endless — the Fanglands’ most renewable resource.',
});
defineEnemy({
  id: 'bat', sprite: 'bat', behavior: 'swarm', tier: 1, weight: 9,
  hp: 8, speed: 128, dmg: 6, r: 11, xp: 1, tint: '#8a4aa0',
  name: 'Dusk Bat', blurb: 'Fast, flappy, and never alone.',
});
defineEnemy({
  id: 'hornet', sprite: 'hornet', behavior: 'swarm', tier: 1, weight: 8,
  hp: 6, speed: 158, dmg: 5, r: 10, xp: 1, tint: '#ffd83a',
  name: 'Neon Hornet', blurb: 'A furious yellow streak. Blink, and it’s on you.',
});
defineEnemy({
  id: 'spawnling', sprite: 'spawnling', behavior: 'chase', tier: 1, weight: 6,
  hp: 20, speed: 48, dmg: 10, r: 14, xp: 2, tint: '#5fce5a',
  splitInto: 'slime', splitCount: 2,          // splits when killed
  name: 'Spawnling', blurb: 'Kill it once, fight it twice — it splits into slimes.',
});
defineEnemy({
  id: 'cinder', sprite: 'cinder', behavior: 'swarm', tier: 1, weight: 8,
  hp: 10, speed: 142, dmg: 8, r: 11, xp: 2, tint: '#ff7a2a',
  explodeOnDeath: true, explodeColor: '#ff8a3d',   // pops into flame (Wastes signature)
  name: 'Cinder', blurb: 'A running ember that pops into flame at the end. Give it room to die.',
});

// ------------------------------ tier 2: standard ------------------------------
defineEnemy({
  id: 'spider', sprite: 'spider', behavior: 'charger', tier: 2, weight: 5,
  hp: 26, speed: 74, dmg: 12, r: 13, xp: 3, tint: '#6a4a7a',
  name: 'Vault Spider', blurb: 'Winds up, then lunges. The wind-up is your window.',
});
defineEnemy({
  id: 'skeleton', sprite: 'skeleton', behavior: 'shooter', tier: 2, weight: 4,
  hp: 30, speed: 70, dmg: 11, r: 13, xp: 3, tint: '#d8d8e0', shootCd: 2.2,
  name: 'Bone Archer', blurb: 'Dead, armed, and still a decent shot.',
});
defineEnemy({
  id: 'imp', sprite: 'imp', behavior: 'chase', tier: 2, weight: 4,
  hp: 34, speed: 96, dmg: 14, r: 13, xp: 4, tint: '#ff8a3d',
  explodeOnDeath: true, explodeColor: '#ff8a3d',   // fiery detonation
  name: 'Ash Imp', blurb: 'Runs at you grinning. The grin is a fuse.',
});
defineEnemy({
  id: 'sporeling', sprite: 'sporeling', behavior: 'chase', tier: 2, weight: 4,
  hp: 26, speed: 54, dmg: 10, r: 13, xp: 3, tint: '#a86ac9',
  explodeOnDeath: true, explodeColor: '#7dff5c',   // toxic spore burst
  name: 'Sporeling', blurb: 'Bursts into toxic spores — don’t stand in the cloud.',
});
defineEnemy({
  id: 'lurker', sprite: 'lurker', behavior: 'shooter', tier: 2, weight: 4,
  hp: 34, speed: 66, dmg: 12, r: 13, xp: 3, tint: '#3ea0ff', shootCd: 1.9,
  name: 'Deep Lurker', blurb: 'Hangs back and snipes from the dark.',
});
defineEnemy({
  id: 'hound', sprite: 'hound', behavior: 'charger', tier: 2, weight: 5,
  hp: 40, speed: 102, dmg: 14, r: 13, xp: 4, tint: '#ff7a2a',
  name: 'Ember Hound', blurb: 'Runs you down in straight, burning lines.',
});
defineEnemy({
  id: 'wisp', sprite: 'wisp', behavior: 'shooter', tier: 2, weight: 4,
  hp: 40, speed: 90, dmg: 13, r: 12, xp: 5, tint: '#6fd6ff', shootCd: 1.7,
  name: 'Gleam Wisp', blurb: 'A drifting light that shoots back.',
});
defineEnemy({
  id: 'wraith', sprite: 'wraith', behavior: 'orbiter', tier: 2, weight: 4,
  hp: 30, speed: 116, dmg: 12, r: 12, xp: 4, tint: '#c8d8ff', ghost: true, orbitPref: 210,
  name: 'Pale Wraith', blurb: 'Circles at arm’s length, half in this world.',
});
defineEnemy({
  id: 'voideye', sprite: 'voideye', behavior: 'shooter', tier: 2, weight: 4,
  hp: 44, speed: 68, dmg: 14, r: 12, xp: 5, tint: '#ff2d9b', shootCd: 1.5,
  name: 'Void Eye', blurb: 'It sees you. Then it fires.',
});

// -------------------------------- tier 3: tough -------------------------------
defineEnemy({
  id: 'charger', sprite: 'charger', behavior: 'charger', tier: 3, weight: 3,
  hp: 60, speed: 90, dmg: 16, r: 15, xp: 5, tint: '#7a4ac0',
  name: 'Crystal Charger', blurb: 'All momentum, no brakes.',
});
defineEnemy({
  id: 'golem', sprite: 'golem', behavior: 'brute', tier: 3, weight: 2,
  hp: 165, speed: 40, dmg: 20, r: 20, xp: 10, gold: 1, tint: '#5a7a9a',
  shardBurst: 8,                               // radial shard shrapnel on death
  name: 'Shard Golem', blurb: 'A walking quarry — shatters into shrapnel when it falls.',
});
defineEnemy({
  id: 'phantom', sprite: 'phantom', behavior: 'phantom', tier: 3, weight: 2,
  hp: 72, speed: 82, dmg: 16, r: 13, xp: 6, tint: '#c9a2ff', ghost: true,
  name: 'Phantom', blurb: 'Fades in and out of reach. Strike while it’s solid.',
});

// ------------------------------- tier 4: heavy --------------------------------
defineEnemy({
  id: 'brute', sprite: 'brute', behavior: 'brute', tier: 4, weight: 1,
  hp: 130, speed: 46, dmg: 20, r: 22, xp: 8, gold: 1, tint: '#a8512e',
  name: 'Waste Brute', blurb: 'Slow, huge, and entirely unimpressed by your arsenal.',
});
